import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import CustomNoResultsOverlay from '../shared/CustomNoResultsOverlay';
import CustomLoading from '../shared/CustomLoading';
import { AnalyticsEvents, trackAnalyticsEvent } from '../../utils/analytics';

const CenteredCell = ({ children }) => (
	<Stack justifyContent='center' sx={{ height: '100%', py: 2 }}>
		{children}
	</Stack>
);

const ValueAddedProgramLinkRenderer = ({ href, children }) => {
	const target = href.includes('ashlink.com') ? '_self' : '_blank';

	return (
		<a
			href={href}
			target={target}
			rel='noreferrer'
			data-analytics-value-added-program-link
			onClick={() => {
				const parsedHref = (() => {
					try {
						return new URL(href);
					} catch {
						return null;
					}
				})();
				trackAnalyticsEvent(
					AnalyticsEvents.VALUE_ADDED_PROGRAM_LINK_CLICKED,
					{
						page_key: 'value_added_program',
						page_path: '/resources/valueAddedProgram',
						link_kind: 'markdown',
						link_host: parsedHref?.host,
						link_path: parsedHref?.pathname,
					}
				);
			}}
		>
			{children}
		</a>
	);
};

CenteredCell.propTypes = {
	children: PropTypes.node,
};

ValueAddedProgramLinkRenderer.propTypes = {
	href: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};

const ValueAddedProgramList = ({
	valueAddedVendors = {},
	valueAddedVendorsIsLoading,
	onVendorClick,
}) => {
	const {
		data: { data: { entries = [] } = {}, resultCode = 0 } = {},
		statusCode,
	} = valueAddedVendors;
	const columns = [
		{
			field: 'displayName',
			sortable: false,
			flex: 2,
			minWidth: 200,
			hideable: false,
			renderHeader: () => (
				<Typography variant='body2'>Value Added Vendor</Typography>
			),
			renderCell: params => (
				<CenteredCell>
					<Button
						data-analytics-value-added-vendor-select-button
						onClick={() => onVendorClick(params.row.id)}
						sx={{ textTransform: 'none' }}
					>
						<Stack direction='column'>
							<img
								src={params.row.imageUrl}
								alt={`${params.row.displayName} logo`}
								width='180'
							/>
							{params.row.displayName}
						</Stack>
					</Button>
				</CenteredCell>
			),
		},
		{
			field: 'description',
			sortable: false,
			renderHeader: () => (
				<Typography variant='body2'>Products and Services</Typography>
			),
			renderCell: params => (
				<CenteredCell>
					<Typography
						variant='body2'
						sx={{ wordBreak: 'break-word' }}
					>
						<ReactMarkdown
							components={{
								a: ValueAddedProgramLinkRenderer,
								p: ({ children }) => <>{children}</>,
							}}
						>
							{params.row.description}
						</ReactMarkdown>
					</Typography>
				</CenteredCell>
			),
			flex: 3,
			minWidth: 175,
			hideable: false,
		},
		{
			field: 'discount',
			sortable: false,
			renderHeader: () => (
				<Typography variant='body2'>
					ASH Practitioner Discount
				</Typography>
			),
			renderCell: params => (
				<CenteredCell>
					<Box my={3}>
						<Typography
							variant='body2'
							gutterBottom
							sx={{ wordBreak: 'break-word' }}
						>
							<ReactMarkdown
								components={{
									a: ValueAddedProgramLinkRenderer,
									p: ({ children }) => <>{children}</>,
								}}
							>
								{params.row.providerDiscount}
							</ReactMarkdown>
						</Typography>

						{params.row.howToRedeemDiscount && (
							<Typography
								variant='body2'
								sx={{ wordBreak: 'break-word' }}
							>
								<Box
									component='span'
									fontWeight='fontWeightBold'
								>
									How To Redeem:
								</Box>{' '}
								<ReactMarkdown
									components={{
										a: ValueAddedProgramLinkRenderer,
										p: ({ children }) => <>{children}</>,
									}}
								>
									{params.row.howToRedeemDiscount}
								</ReactMarkdown>
							</Typography>
						)}
					</Box>
				</CenteredCell>
			),
			flex: 3,
			minWidth: 200,
			hideable: false,
		},
	];

	return (
		<>
			<Typography fontWeight='fontWeightBold' gutterBottom>
				American Specialty Health (ASH) is pleased to introduce the
				Value Added Products Program. By selecting the vendor of
				interest below, you can link directly to their Web site, to
				learn more about their product offerings, as well as place
				orders. Practitioners can take advantage of discounts of 15% or
				more in the areas of malpractice insurance, X-ray equipment and
				supplies, durable medical equipment, continuing education
				(including CEU for license renewal), and much more.
			</Typography>
			<Box mb={2}>
				<DataGrid
					columns={columns}
					disableColumnMenu
					disableColumnFilter
					disableColumnResize
					disableColumnSelector
					rowSelection={false}
					hideFooter
					autoHeight
					initialState={{
						sorting: {
							sortModel: [
								{
									field: 'displayName',
								},
							],
						},
					}}
					getRowHeight={() => 'auto'}
					rows={entries}
					loading={valueAddedVendorsIsLoading}
					slots={{
						noRowsOverlay: CustomNoResultsOverlay,
						loadingOverlay: CustomLoading,
					}}
					slotProps={{
						noRowsOverlay: {
							resultCode,
							statusCode,
						},
					}}
				/>
			</Box>
			<Typography gutterBottom>
				ASH is not and will not solicit any fees, rebates or commissions
				from external value-added vendors. This program was created with
				the goal of assisting ASH practitioners in reducing their
				monthly costs and overhead by taking advantage of the discounts
				offered through participating external value-added vendors. ASH
				does not warrant, represent, or guarantee the quality and/or
				performance of the products or services offered by its
				Value-Added vendors. Buyers assume all risk associated with the
				use and purchase of all products and services.
			</Typography>
		</>
	);
};

export default ValueAddedProgramList;

ValueAddedProgramList.propTypes = {
	valueAddedVendors: PropTypes.object,
	valueAddedVendorsIsLoading: PropTypes.bool.isRequired,
	onVendorClick: PropTypes.func.isRequired,
};
